import axios from 'axios';
import {
  AirflowAnalysisResponse,
  AirflowAnalysisResult,
  AnalysisResponse,
  BuildingLocation,
  FengShuiReportResponse,
  ImageAnalysisResponse,
  ImageAnalysisResult,
} from '../types';
import { appConfig } from '../config';
import { HouseLuckChart } from '../utils/houseLuckChart';

const apiClient = axios.create({
  baseURL: appConfig.apiBaseUrl,
});

export type PublicFeatureFlags = {
  floorplan_recognition: boolean;
  ai_report: boolean;
  airflow_analysis: boolean;
  raster_scan: boolean;
  environment_analysis: boolean;
  sms_login: boolean;
};

export const defaultPublicFeatureFlags: PublicFeatureFlags = {
  floorplan_recognition: true,
  ai_report: true,
  airflow_analysis: true,
  raster_scan: true,
  environment_analysis: true,
  sms_login: true,
};

export async function fetchPublicFeatures(): Promise<PublicFeatureFlags> {
  const response = await apiClient.get<{ success: boolean; data: PublicFeatureFlags }>('/api/system/features');
  return response.data.data;
}

const compactImageAnalysis = (imageAnalysis: ImageAnalysisResult): ImageAnalysisResult => {
  if (
    !imageAnalysis.yoloAnalysis?.visualizationImage &&
    !imageAnalysis.yoloAnalysis?.airflowVisualizationImage
  ) {
    return imageAnalysis;
  }

  return {
    ...imageAnalysis,
    yoloAnalysis: {
      ...imageAnalysis.yoloAnalysis,
      visualizationImage: undefined,
      airflowVisualizationImage: undefined,
    },
  };
};

/**
 * 上传图片并分析风水
 */
export async function analyzeFengShui(
  file: File,
  houseLuckChart: HouseLuckChart,
  buildingLocation?: BuildingLocation,
): Promise<AnalysisResponse['data']> {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('houseLuckChart', JSON.stringify(houseLuckChart));
  if (buildingLocation) {
    formData.append('buildingLocation', JSON.stringify(buildingLocation));
  }

  const response = await apiClient.post<AnalysisResponse>(
    '/api/fengshui/analyze',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  if (!response.data.success) {
    throw new Error('Analysis failed');
  }

  return response.data.data;
}

/**
 * 仅上传图片并完成户型识别
 */
export async function analyzeImage(file: File, buildingLocation?: BuildingLocation): Promise<ImageAnalysisResult> {
  const formData = new FormData();
  formData.append('image', file);
  if (buildingLocation) {
    formData.append('buildingLocation', JSON.stringify(buildingLocation));
  }

  const response = await apiClient.post<ImageAnalysisResponse>(
    '/api/fengshui/analyze-image',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  if (!response.data.success) {
    throw new Error('Image analysis failed');
  }

  return response.data.data;
}

/**
 * 根据已识别户型和校准后的朝向模型生成空间分析
 */
export async function analyzeFengShuiReport(
  imageAnalysis: ImageAnalysisResult,
  houseLuckChart: HouseLuckChart,
  buildingLocation?: BuildingLocation,
): Promise<FengShuiReportResponse['data']> {
  const response = await apiClient.post<FengShuiReportResponse>(
    '/api/fengshui/analyze-report',
    {
      imageAnalysis: compactImageAnalysis(imageAnalysis),
      houseLuckChart,
      buildingLocation,
    }
  );

  if (!response.data.success) {
    throw new Error('Feng shui report failed');
  }

  return response.data.data;
}

/**
 * 根据已识别户型生成气流 Graph + Flow 可视化数据
 */
export async function analyzeAirflow(
  imageAnalysis: ImageAnalysisResult,
): Promise<AirflowAnalysisResult> {
  const response = await apiClient.post<AirflowAnalysisResponse>(
    '/api/fengshui/analyze-airflow',
    {
      imageAnalysis: compactImageAnalysis(imageAnalysis),
    }
  );

  if (!response.data.success) {
    throw new Error('Airflow analysis failed');
  }

  return response.data.data;
}
